import { get, reduce, pickBy, isEmpty, compact } from 'lodash';
import { createSelector } from 'reselect';

import { calculateRate } from '../../utils/common-utils';

export const allDomainsInAggDataSelector = state => get( state, 'aggResults.allDomainIds', [] );
const allSenderIdsInAggDataSelector = state => get( state, 'aggResults.allSenderIds', [] );
export const aggResultsIsLoadingSelector = state => get( state, 'aggResults.isLoading', false );
export const aggResultsParamsSelector = state => get( state, 'aggResults.params', {} );
export const aggDataByDomainIdSelector = state => get( state, 'aggResults.byDomainId', {} );
export const aggDataBySenderIdSelector = state => get( state, 'aggResults.bySenderId', {} );

const flattenDomainData = ( { spf, dkim, dmarc, ...row } ) => {
    const calculateSenderVerifiedValue = ( value = 0 ) => {
        return get( row, 'sender.verified', false ) || row.verified ? value : 0;
    };

    const calculateSenderUnverifiedValue = ( value ) => {
        return get( row, 'sender.verified', false ) || row.verified ? 0 : value;
    };
    return {
        ...row,
        authorizedMailVolume: calculateSenderVerifiedValue( row.totalMailCount ),
        blockedMailCount: calculateSenderVerifiedValue( row.blockedMailCount ),
        dkimAlignCount: calculateSenderVerifiedValue( dkim.alignCount ),
        dkimFailCount: calculateSenderVerifiedValue( dkim.failCount ),
        dkimMisalignCount: calculateSenderVerifiedValue( dkim.misalignCount ),
        dkimNeutralCount: calculateSenderVerifiedValue( dkim.neutralCount ),
        dkimPassAlignCount: calculateSenderVerifiedValue( dkim.passAlignCount ),
        dkimPassCount: calculateSenderVerifiedValue( dkim.passCount ),
        dkimTotalCount: calculateSenderVerifiedValue( dkim.totalCount ),
        dmarcDoublePassCount: calculateSenderVerifiedValue( dmarc.doublePassCount ),
        dmarcFailCount: calculateSenderVerifiedValue( dmarc.failCount ),
        dmarcPassCount: calculateSenderVerifiedValue( dmarc.passCount ),
        spfAlignCount: calculateSenderVerifiedValue( spf.alignCount ),
        spfFailCount: calculateSenderVerifiedValue( spf.failCount ),
        spfMisalignCount: calculateSenderVerifiedValue( spf.misalignCount ),
        spfNeutralCount: calculateSenderVerifiedValue( spf.neutralCount ),
        spfPassAlignCount: calculateSenderVerifiedValue( spf.passAlignCount ),
        spfPassCount: calculateSenderVerifiedValue( spf.passCount ),
        spfTotalCount: calculateSenderVerifiedValue( spf.totalCount ),
        unauthorizedBlockedMailCount: calculateSenderUnverifiedValue( row.blockedMailCount ),
        unauthorizedDMARCPassCount: calculateSenderUnverifiedValue( dmarc.passCount ),
        unauthorizedMailVolume: calculateSenderUnverifiedValue( row.totalMailCount ),
    };
};

const consolidateAggData = ( aggData = {} ) => {
    return reduce( aggData, ( result, value ) => {
        const flatDomainData = flattenDomainData( value );
        Object.keys( flatDomainData ).forEach( ( key ) => {
            if( key === 'sender' || key === 'verified' ){
                return result;
            }
            return result[key] += flatDomainData[key];
        } );
        return result;
    },
    {
        authorizedMailVolume: 0,
        blockedMailCount: 0,
        dkimAlignCount: 0,
        dkimFailCount: 0,
        dkimMisalignCount: 0,
        dkimNeutralCount: 0,
        dkimPassAlignCount: 0,
        dkimPassCount: 0,
        dkimTotalCount: 0,
        dmarcDoublePassCount: 0,
        dmarcFailCount: 0,
        dmarcPassCount: 0,
        ipCount: 0,
        spfAlignCount: 0,
        spfFailCount: 0,
        spfMisalignCount: 0,
        spfNeutralCount: 0,
        spfPassAlignCount: 0,
        spfPassCount: 0,
        spfTotalCount: 0,
        totalMailCount: 0,
        unauthorizedBlockedMailCount: 0,
        unauthorizedDMARCPassCount: 0,
        unauthorizedMailVolume: 0,
    } );
};

const consolidateDomainAggData = ( aggData ) => {
    const consolidatedData = consolidateAggData( aggData.countsBySender );
    consolidatedData.domainName = aggData.domainName;
    return consolidatedData;
};

const consolidateSenderAggData = ( { countsByDomain, name, id } ) => {
    // remove unverified senders
    const verifiedCountsByDomain = pickBy( countsByDomain, ( counts ) => {
        return counts.verified;
    } );

    if( isEmpty( verifiedCountsByDomain ) ){
        // bail early if no verified traffic for this sender
        return null;
    }
    const consolidatedData = consolidateAggData( countsByDomain );

    const domainCount = Object.keys( verifiedCountsByDomain ).length;
    return {
        ...consolidatedData,
        actions: 0,
        blockRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.blockedMailCount ),
        dkimAlignRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.dkimAlignCount ),
        dkimPassAlignRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.dkimPassAlignCount ),
        dkimPassRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.dkimPassCount ),
        dmarcDoublePassRate: calculateRate(
            consolidatedData.authorizedMailVolume,
            consolidatedData.dmarcDoublePassCount
        ),
        dmarcPassRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.dmarcPassCount ),
        domainCount,
        name,
        senderId: id,
        spfAlignRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.spfAlignCount ),
        spfPassAlignRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.spfPassAlignCount ),
        spfPassRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.spfPassCount ),
        unauthorizedBlockRate: calculateRate(
            consolidatedData.unauthorizedMailVolume,
            consolidatedData.unauthorizedBlockedMailCount
        ),
        unauthorizedDMARCPassRate: calculateRate(
            consolidatedData.unauthorizedMailVolume,
            consolidatedData.unauthorizedDMARCPassCount
        ),
        verifiedDmarcPassRate: calculateRate( consolidatedData.authorizedMailVolume, consolidatedData.dmarcPassCount ),
    };
};

export const flatAggregateResultsByDomainSelector = createSelector(
    allDomainsInAggDataSelector,
    aggDataByDomainIdSelector,
    ( domains, aggDataByDomainId ) => {
        return domains.map( domainId => consolidateDomainAggData( aggDataByDomainId[domainId] ) );
    }
);

export const flatAggregateResultsByVerifiedSenderSelector = createSelector(
    allSenderIdsInAggDataSelector,
    aggDataBySenderIdSelector,
    ( senderIds, aggDataBySenderId ) => {
        return compact( senderIds.map( senderId => consolidateSenderAggData( aggDataBySenderId[senderId] ) ) );
    }
);
